"use client";

import type { LogoCloudContent } from "@/components/landing/registry/content";
import { pickBi } from "@/components/landing/registry/locale";
import {
	Eyebrow,
	MotionReveal,
	SectionHeader,
	useSectionMotion,
} from "@/components/landing/shared";

interface LogoCloudInnerProps {
	content: LogoCloudContent;
	locale: string;
}

/**
 * Static centered wrap of partner/customer logos — a non-animated
 * companion to `TrustStrip`'s scrolling ticker. Falls back to a pill with
 * the logo's label when no image has been uploaded.
 */
function LogoCloudInner(prop: LogoCloudInnerProps) {
	const { content, locale } = prop;
	const motion = useSectionMotion();
	const eyebrow = pickBi(content.eyebrow, locale);
	const title = pickBi(content.title, locale);
	const logos = content.logos.filter((logo) => logo.enabled);

	return (
		<>
			<MotionReveal motion={motion}>
				<SectionHeader
					eyebrow={eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : undefined}
					title={title}
					wrapperClassName="mx-auto max-w-2xl px-6 text-center"
				/>
			</MotionReveal>

			<div className="mx-auto mt-10 flex max-w-4xl flex-wrap items-center justify-center gap-x-10 gap-y-6 px-6">
				{logos.map((logo, i) => (
					<MotionReveal key={logo.id} motion={motion} delayOffset={120 + i * 40}>
						{logo.imageUrl ? (
							<img
								src={logo.imageUrl}
								alt={logo.label}
								className="h-8 w-auto opacity-80 grayscale transition hover:opacity-100 hover:grayscale-0"
							/>
						) : (
							<span className="rounded-full border border-border bg-bg/70 px-4 py-2 text-sm font-semibold">
								{logo.label}
							</span>
						)}
					</MotionReveal>
				))}
			</div>
		</>
	);
}

export default LogoCloudInner;
